import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { siteConfig, telHref, waHref } from "@/config/siteConfig";
import { trackEvent } from "@/lib/analytics";
import { Logo } from "@/components/Logo";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";

export const Header = ({ minimal = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const overHero = pathname === "/" || pathname === "/cekici";

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const dark = overHero && !scrolled && !open;
  const linkHref = (hash) => (pathname === "/" ? `#${hash}` : `/#${hash}`);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-40 transition-[background-color,border-color,box-shadow] duration-300 ${
        dark
          ? "border-b border-white/10 bg-ink-950/40 backdrop-blur-md"
          : "border-b border-ink-100 bg-white/90 shadow-sm backdrop-blur-xl"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-5 md:h-[76px]">
        <Logo dark={dark} onClick={() => setOpen(false)} />

        {!minimal && (
          <nav aria-label="Ana menü" className="hidden items-center gap-6 xl:flex">
            {siteConfig.nav.map((item) => (
              <a
                key={item.hash}
                href={linkHref(item.hash)}
                data-testid={`nav-${item.hash}`}
                className={`whitespace-nowrap text-sm font-semibold transition-colors duration-200 hover:text-brand ${
                  dark ? "text-white/85" : "text-neutral-600"
                }`}
              >
                {item.label}
              </a>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-2.5">
          <a
            href={telHref}
            data-testid="header-call-button"
            onClick={() => trackEvent("phone_click", "header")}
            className="hidden min-h-[44px] items-center gap-2 rounded-full bg-brand px-5 py-2.5 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-md shadow-brand/30 transition-[transform,background-color] duration-200 hover:scale-105 hover:bg-brand-dark active:scale-95 sm:inline-flex"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            Hemen Ara
          </a>
          <a
            href={waHref()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-button"
            onClick={() => trackEvent("whatsapp_location_click", "header")}
            className="hidden min-h-[44px] items-center gap-2.5 rounded-full bg-wa px-5 py-2 font-display uppercase leading-none text-white shadow-md shadow-wa/30 transition-[transform,background-color] duration-200 hover:scale-105 hover:bg-wa-dark active:scale-95 lg:inline-flex"
          >
            <WhatsAppIcon className="h-5 w-5" />
            <span>
              <span className="block text-[13px] font-extrabold tracking-wide">WhatsApp'tan</span>
              <span className="mt-0.5 block text-[10px] font-bold tracking-[0.14em] text-white/85">Konum Gönder</span>
            </span>
          </a>
          {!minimal && (
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              aria-label={open ? "Menüyü kapat" : "Menüyü aç"}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className={`grid h-11 w-11 place-items-center rounded-xl transition-colors xl:hidden ${
                dark ? "text-white hover:bg-white/10" : "text-ink-900 hover:bg-ink-100"
              }`}
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          )}
        </div>
      </div>

      {!minimal && open && (
        <nav
          aria-label="Mobil menü"
          data-testid="mobile-menu"
          className="border-t border-ink-100 bg-white px-5 pb-5 pt-3 xl:hidden"
        >
          {siteConfig.nav.map((item) => (
            <a
              key={item.hash}
              href={linkHref(item.hash)}
              data-testid={`mobile-nav-${item.hash}`}
              onClick={() => setOpen(false)}
              className="block border-b border-ink-100/70 py-3.5 font-display text-base font-bold text-ink-900 last:border-0"
            >
              {item.label}
            </a>
          ))}
          <div className="mt-4 grid gap-2.5">
            <a
              href={telHref}
              data-testid="mobile-menu-call-button"
              onClick={() => trackEvent("phone_click", "header")}
              className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-brand py-3 font-display text-base font-extrabold uppercase text-white"
            >
              <Phone className="h-5 w-5" aria-hidden="true" />
              Hemen Ara — {siteConfig.phone.display}
            </a>
            <a
              href={waHref()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-menu-whatsapp-button"
              onClick={() => trackEvent("whatsapp_location_click", "header")}
              className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-wa py-3 font-display text-base font-extrabold uppercase text-white"
            >
              <WhatsAppIcon className="h-5 w-5" />
              WhatsApp'tan Konum Gönder
            </a>
          </div>
        </nav>
      )}
    </header>
  );
};
